// ============================================================================
// USE JOB CONFIG HOOK
// Shared hook for fetching dynamic job configuration (workflow statuses,
// job types, priorities) from the unified /api/v1/job-config endpoint.
// Used by: ServiceJobModal, AppointmentModal, DetailsSidebar,
//          AutomationRuleBuilder, AutomationsList
// Date: February 2026
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/lib/authService';
import {
  DEFAULT_APPOINTMENT_TYPES as SHARED_DEFAULT_APPOINTMENT_TYPES,
  DEFAULT_JOB_TYPES as SHARED_DEFAULT_JOB_TYPES,
  getDefaultTypeOptions as getSharedDefaultTypeOptions,
  getHiddenTypeKeys,
  normalizeTypeOptionsForItemType,
  type ConfigurableTypeOption,
} from '@/lib/work-items/appointmentTypeConfig';
import {
  DEFAULT_EFFORT_CONFIG,
  normalizeEffortConfig,
  type EffortConfig,
} from '@/lib/work-items/effortConfig';
import {
  ensureRequiredOperationalWorkflowStatuses,
  getDefaultAppointmentWorkflowStatuses,
  getDefaultOperationalWorkflowStatuses,
  getDefaultOperationalWorkflowStatusesForItemType,
} from '@/lib/work-items/operationalStatusConfig';

// ============================================================================
// TYPES
// ============================================================================

export interface WorkflowStatus {
  key: string;
  label: string;
  color: string;
  description?: string;
  isStart?: boolean;
  isEnd?: boolean;
  isSystem?: boolean;
  allow_billing?: boolean;
  /** Which statuses are allowed to transition TO this status. If set, drives next-step dropdowns. */
  allowed_from?: string[];
}

export type JobType = ConfigurableTypeOption;

export interface PriorityOption {
  key: string;
  label: string;
  color: string;
  description?: string;
}

export interface ResolveChecklistConfig {
  enabled: boolean;
  enforced: boolean;
}

export interface JobConfig {
  workflow_statuses: WorkflowStatus[];
  job_types: JobType[];
  priorities: PriorityOption[];
  effort_config: EffortConfig;
  resolve_checklist: ResolveChecklistConfig;
  /** When true, work items of this type must have a responsible manager
   * (work_items.manager_id) — enforced in create/edit UIs. */
  require_manager: boolean;
  has_transition_rules?: boolean;
  is_default: {
    workflow_statuses: boolean;
    job_types: boolean;
    priorities: boolean;
    effort_config: boolean;
  };
}

// ============================================================================
// DEFAULTS (used as fallback when API is unavailable)
// ============================================================================

export const DEFAULT_PRIORITIES: PriorityOption[] = [
  { key: 'low', label: 'Low', color: '#22c55e', description: 'Non-urgent, can be scheduled for later' },
  { key: 'medium', label: 'Medium', color: '#eab308', description: 'Standard priority' },
  { key: 'high', label: 'High', color: '#f97316', description: 'Should be addressed soon' },
  { key: 'urgent', label: 'Urgent', color: '#ef4444', description: 'Requires immediate attention' },
  { key: 'critical', label: 'Critical', color: '#dc2626', description: 'Business-critical, top priority' },
];

export const DEFAULT_JOB_TYPES: JobType[] = SHARED_DEFAULT_JOB_TYPES;

export const DEFAULT_APPOINTMENT_TYPES: JobType[] = SHARED_DEFAULT_APPOINTMENT_TYPES;

export function getDefaultTypeOptions(itemType: string): JobType[] {
  return getSharedDefaultTypeOptions(itemType);
}

export const DEFAULT_WORKFLOW_STATUSES: WorkflowStatus[] = getDefaultOperationalWorkflowStatuses();

export const DEFAULT_APPOINTMENT_WORKFLOW_STATUSES: WorkflowStatus[] = getDefaultAppointmentWorkflowStatuses();

export function getDefaultWorkflowStatuses(itemType: string): WorkflowStatus[] {
  return getDefaultOperationalWorkflowStatusesForItemType(itemType);
}

// ============================================================================
// PRIORITY DISPLAY HELPERS (for DetailsSidebar-style rendering)
// ============================================================================

/** Map a priority key to Tailwind display classes (compatible with DetailsSidebar format) */
export function getPriorityDisplay(priorityKey: string, priorities: PriorityOption[]) {
  const priority = priorities.find(p => p.key === priorityKey);
  if (!priority) {
    return {
      label: priorityKey.charAt(0).toUpperCase() + priorityKey.slice(1),
      color: 'bg-gray-100 text-gray-700 border-gray-300',
      dot: 'bg-gray-500',
      hexColor: '#6b7280',
    };
  }

  // Map hex color to Tailwind-ish classes for backwards compat
  const colorMap: Record<string, { bg: string; dot: string }> = {
    '#22c55e': { bg: 'bg-green-100 text-green-700 border-green-300', dot: 'bg-green-500' },
    '#eab308': { bg: 'bg-yellow-100 text-yellow-700 border-yellow-300', dot: 'bg-yellow-500' },
    '#f97316': { bg: 'bg-orange-100 text-orange-700 border-orange-300', dot: 'bg-orange-500' },
    '#ef4444': { bg: 'bg-red-100 text-red-700 border-red-300', dot: 'bg-red-500' },
    '#dc2626': { bg: 'bg-red-200 text-red-800 border-red-400', dot: 'bg-red-600' },
  };

  const mapped = colorMap[priority.color];

  return {
    label: priority.label,
    color: mapped?.bg || 'bg-gray-100 text-gray-700 border-gray-300',
    dot: mapped?.dot || 'bg-gray-500',
    hexColor: priority.color,
  };
}

// ============================================================================
// CACHE (module-level simple cache to avoid redundant API calls across components)
// ============================================================================

interface CacheEntry {
  data: JobConfig;
  timestamp: number;
}

const configCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60_000; // 1 minute

/** Cache key includes the workflow scope so scoped and default configs never mix. */
function configCacheKey(itemType: string, workflowContext?: string | null): string {
  return `${workflowContext?.trim() || 'default'}:${itemType}`;
}

function getCachedConfig(itemType: string, workflowContext?: string | null): JobConfig | null {
  const entry = configCache.get(configCacheKey(itemType, workflowContext));
  if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS) {
    return entry.data;
  }
  return null;
}

function setCachedConfig(itemType: string, data: JobConfig, workflowContext?: string | null) {
  configCache.set(configCacheKey(itemType, workflowContext), { data, timestamp: Date.now() });
}

/** Invalidate cache for a specific item type (call after saving config) */
export function invalidateJobConfigCache(itemType?: string, workflowContext?: string | null) {
  if (itemType) {
    configCache.delete(configCacheKey(itemType, workflowContext));
  } else {
    configCache.clear();
  }
}

// ============================================================================
// HOOK
// ============================================================================

interface UseJobConfigOptions {
  /** Item type to fetch config for (default: 'job') */
  itemType?: string;
  /** Workflow scope ('visitor-management', …) — resolves scoped config first,
   *  falling back to the client-wide 'default' row. Omit for legacy behavior. */
  workflowContext?: string | null;
  /** Skip fetching on mount (useful if you want to call refresh manually) */
  skip?: boolean;
}

export function useJobConfig(options: UseJobConfigOptions = {}) {
  const { itemType = 'job', workflowContext = null, skip = false } = options;

  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>(getDefaultWorkflowStatuses(itemType));
  const [jobTypes, setJobTypes] = useState<JobType[]>(getDefaultTypeOptions(itemType));
  const [priorities, setPriorities] = useState<PriorityOption[]>(DEFAULT_PRIORITIES);
  const [effortConfig, setEffortConfig] = useState<EffortConfig>(DEFAULT_EFFORT_CONFIG);
  const [resolveChecklist, setResolveChecklist] = useState<ResolveChecklistConfig>({ enabled: false, enforced: false });
  const [requireManager, setRequireManager] = useState(false);
  const [hasTransitionRules, setHasTransitionRules] = useState(false);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);

  const getAuthToken = useCallback(() => {
    return authService.getTokens()?.access_token || '';
  }, []);

  const fetchConfig = useCallback(async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh) {
      const cached = getCachedConfig(itemType, workflowContext);
      if (cached) {
        setWorkflowStatuses(ensureRequiredOperationalWorkflowStatuses(cached.workflow_statuses, itemType));
        setJobTypes(normalizeTypeOptionsForItemType(itemType, cached.job_types));
        setPriorities(cached.priorities);
        setEffortConfig(normalizeEffortConfig(cached.effort_config));
        setResolveChecklist(cached.resolve_checklist || { enabled: false, enforced: false });
        setRequireManager(Boolean(cached.require_manager));
        setHasTransitionRules(Boolean(cached.has_transition_rules));
        setLoading(false);
        return cached;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const token = getAuthToken();
      if (!token) {
        setLoading(false);
        return null;
      }

      const scopeParam = workflowContext?.trim()
        ? `&workflow_context=${encodeURIComponent(workflowContext.trim())}`
        : '';
      const response = await fetch(`/api/v1/job-config?item_type=${encodeURIComponent(itemType)}${scopeParam}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load job configuration');
      }

      const data = await response.json();
      if (data.success && data.data) {
        // Load transitions from work_item_workflow_transitions table
        const transitionsResponse = await fetch(`/api/v1/work-items/transitions?item_type=${encodeURIComponent(itemType)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!transitionsResponse.ok) {
          console.warn('[useJobConfig] Transitions fetch failed:', transitionsResponse.status, transitionsResponse.statusText);
        }
        const transitionsData = transitionsResponse.ok ? await transitionsResponse.json() : { success: false };
        const transitions = transitionsData.success ? transitionsData.data : [];

        // Build allowed_from map from transitions
        const allowedFromMap = new Map<string, string[]>();
        transitions.forEach((transition: { from_status: string; to_status: string }) => {
          if (!allowedFromMap.has(transition.to_status)) {
            allowedFromMap.set(transition.to_status, []);
          }
          allowedFromMap.get(transition.to_status)!.push(transition.from_status);
        });

        // Add allowed_from to workflow statuses
        // Only set allowed_from when transitions exist for this status;
        // undefined means "not configured" which the UI treats as permissive.
        const workflowStatusesWithAllowedFrom = ensureRequiredOperationalWorkflowStatuses(data.data.workflow_statuses, itemType).map((status: WorkflowStatus) => ({
          ...status,
          allowed_from: allowedFromMap.has(status.key) ? allowedFromMap.get(status.key) : undefined,
        }));

        const config: JobConfig = {
          ...data.data,
          workflow_statuses: workflowStatusesWithAllowedFrom,
          job_types: normalizeTypeOptionsForItemType(itemType, data.data.job_types),
          effort_config: normalizeEffortConfig(data.data.effort_config),
          resolve_checklist: data.data.resolve_checklist || { enabled: false, enforced: false },
          require_manager: Boolean(data.data.require_manager),
        };
        setWorkflowStatuses(config.workflow_statuses);
        setJobTypes(config.job_types);
        setPriorities(config.priorities);
        setEffortConfig(config.effort_config);
        setResolveChecklist(config.resolve_checklist);
        setRequireManager(config.require_manager);
        setHasTransitionRules(Boolean(config.has_transition_rules) || transitions.length > 0);
        setCachedConfig(itemType, config, workflowContext);
        return config;
      }
    } catch (err: unknown) {
      console.error('[useJobConfig] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load job configuration');
      // Keep defaults on error
    } finally {
      setLoading(false);
    }

    return null;
  }, [itemType, workflowContext, getAuthToken]);

  // Reset defaults and fetch once when the item type changes.
  useEffect(() => {
    setWorkflowStatuses(getDefaultWorkflowStatuses(itemType));
    setJobTypes(getDefaultTypeOptions(itemType));
    setPriorities(DEFAULT_PRIORITIES);
    setEffortConfig(DEFAULT_EFFORT_CONFIG);
    setResolveChecklist({ enabled: false, enforced: false });
    setRequireManager(false);
    setHasTransitionRules(false);
    setError(null);
    setLoading(!skip);

    if (skip) {
      return;
    }

    void fetchConfig();
  }, [itemType, workflowContext, skip, fetchConfig]);

  /** Force refresh from API */
  const refresh = useCallback(() => {
    invalidateJobConfigCache(itemType, workflowContext);
    return fetchConfig(true);
  }, [fetchConfig, itemType, workflowContext]);

  return {
    workflowStatuses,
    jobTypes,
    priorities,
    effortConfig,
    resolveChecklist,
    requireManager,
    hasTransitionRules,
    loading,
    error,
    refresh,
  };
}

export default useJobConfig;

// ============================================================================
// HIDDEN TYPE KEYS HOOK
// Fetches type configs for multiple item types and returns the keys of types
// where visible_to_customer === false. Used by customer portal views to
// exclude hidden work items from display.
// ============================================================================

export function useHiddenItemTypeKeys(itemTypes: string[], enabled: boolean = true) {
  const itemTypesKey = itemTypes.join(',');
  const [loadedKeys, setLoadedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const getAuthToken = useCallback(() => {
    return authService.getTokens()?.access_token || '';
  }, []);

  useEffect(() => {
    if (!enabled || itemTypesKey.length === 0) {
      return;
    }

    let cancelled = false;

    const loadHiddenKeys = async () => {
      try {
        setLoading(true);
        const token = getAuthToken();
        if (!token) {
          if (!cancelled) setLoading(false);
          return;
        }

        const types = itemTypesKey.split(',').filter(Boolean);
        const results = await Promise.all(
          types.map(async (itemType) => {
            // Check cache first
            const cached = getCachedConfig(itemType);
            if (cached) {
              return getHiddenTypeKeys(normalizeTypeOptionsForItemType(itemType, cached.job_types));
            }

            try {
              const response = await fetch(
                `/api/v1/job-config?item_type=${encodeURIComponent(itemType)}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (!response.ok) return [];
              const data = await response.json();
              if (data.success && data.data) {
                setCachedConfig(itemType, data.data);
                return getHiddenTypeKeys(normalizeTypeOptionsForItemType(itemType, data.data.job_types));
              }
              return [];
            } catch {
              return [];
            }
          })
        );

        if (!cancelled) {
          const merged = Array.from(new Set(results.flat()));
          setLoadedKeys(merged);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadHiddenKeys();

    return () => {
      cancelled = true;
    };
  }, [itemTypesKey, enabled, getAuthToken]);

  // Derive effective keys: empty when disabled or no item types
  const hiddenKeys = enabled && itemTypesKey.length > 0 ? loadedKeys : [];

  return { hiddenKeys, loading };
}
